import type {MetaHTMLAttributes, LinkHTMLAttributes} from 'react';
import type {IconType} from '@/atoms/Icon/typings';

/**
 * @description generates the en-GB title parts based on the user's first name and basket count.
 * because the basket may be empty, the second part of the title is optional. This is needed to
 * because the second part of the title displayed on a new line on mobile devices.
 * @param {Object} param parameters object.
 * @param {string|null} param.userFirstName user's first name if available.
 * @param {number} param.basketCount number of items in the basket.
 * @return {[string, (string|undefined)]} the tuple of page title parts.
 */
export const getPageTitleContent = ({
  userFirstName,
  basketCount,
}: {
  userFirstName: string | null;
  basketCount: number;
}): [string, string?] => {
  if (basketCount === 0) {
    return userFirstName?.length
      ? [`${userFirstName}, your basket is empty`]
      : ['Your basket is empty'];
  }

  return userFirstName?.length
    ? [`${userFirstName}, ${basketCount} item${basketCount > 1 ? 's' : ''} `, 'in your basket']
    : [`${basketCount} item${basketCount > 1 ? 's' : ''} `, 'in your basket'];
};

export const basketUspContent = [
  {
    id: 'money-back-guarantee',
    icon: null,
    image: {
      src: '/img/v2/shield2.svg',
      width: 24,
      height: 29,
      alt: 'shield',
    },
    title: 'Money back guarantee',
    subTitle: 'We guarantee satisfaction with our free, no questions asked returns policy.',
  },
  {
    id: 'secure-payments',
    icon: {type: 'card' as IconType},
    image: null,
    title: 'Secure payments',
    subTitle: 'All payments are 100% secured through our trusted partners.',
  },
  {
    id: 'dedicated-support',
    icon: {type: 'comment' as IconType, fontSize: 20},
    image: null,
    title: 'Dedicated support',
    subTitle: 'Have total peace of mind, knowing our Support Team is always here to help.',
  },
];

export const localMessages = {
  itemDeleteFail: 'We are unable to remove this item. Try again later.',
  updateBasketFail: 'We are unable to update your basket information. Please, try again later.',
  applyPromoFail: `There was a network error. Your promo code wasn't applied. Please try again later.`,
};

const metaTags: (MetaHTMLAttributes<HTMLMetaElement> & {id: string})[] = [
  {
    name: 'description',
    content:
      'The art marketplace. Buy wall art, original paintings, sculptures, limited edition prints and photography directly from independent artists.',
    /**
     * @description key is used to identify the metadata in the page head to avoid duplication
     * @link https://nextjs.org/docs/pages/api-reference/components/head#avoid-duplicated-tags
     */
    id: 'basket-page-description',
  },
  {
    name: 'robots',
    content: 'noindex,nofollow',
    id: 'basket-page-robots',
  },

  {
    property: 'og:site_name',
    content: 'Artfinder',
    id: 'basket-page-og-site-name',
  },
  {
    property: 'og:title',
    content: 'Buy Original Art, Wall Art & Art Prints | Artfinder',
    id: 'basket-page-og-title',
  },
  {
    property: 'og:description',
    content:
      'The art marketplace. Buy wall art, original paintings, sculptures, limited edition prints and photography directly from independent artists.',

    id: 'basket-page-og-description',
  },
  {
    property: 'og:image',
    content:
      'https://d3rf6j5nx5r04a.cloudfront.net/xmewmbFMA1h9nOHCcoO_HcUCiEU=/1200x630/content/f/3/c628317dd64942fd8f0b6b49c5d8f5f5.jpg',

    id: 'basket-page-og-image',
  },
  {
    name: 'twitter:image',
    content:
      'https://d3rf6j5nx5r04a.cloudfront.net/xmewmbFMA1h9nOHCcoO_HcUCiEU=/1200x630/content/f/3/c628317dd64942fd8f0b6b49c5d8f5f5.jpg',
    id: 'basket-page-twitter-image',
  },
  {
    name: 'twitter:description',
    content:
      'The art marketplace. Buy wall art, original paintings, sculptures, limited edition prints and photography directly from independent artists.',
    id: 'basket-page-twitter-description',
  },
  {
    name: 'twitter:card',
    content: 'summary_large_image',
    id: 'basket-page-twitter-card',
  },
  {
    name: 'twitter:site',
    content: '@artfinder',
    id: 'basket-page-twitter-site',
  },
];

const links: (LinkHTMLAttributes<HTMLLinkElement> & {id: string})[] = [
  {
    id: 'basket-page-favicon-link',
    rel: 'icon',
    href: '/favicon.ico',
  },
  {
    id: 'basket-page-canonical-link',
    rel: 'canonical',
    href: `${process.env.HOST}/basket/`,
  },
];

/**
 * @description metadata for the basket page.
 * @return {Object} metadata object.
 */
export const metaData = () => ({
  title: 'Your shopping basket | Artfinder',
  metaTags,
  links,
});
